import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import { invitations } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { LogoSymbol } from '@/shared/ui/Logo';
import { Skeleton } from '@/shared/ui/Skeleton';

import { POLITICA_DE_SENHA } from './senha';

/**
 * Aceite de convite.
 *
 * A tela e publica: quem chega aqui ainda nao tem sessao, e o segredo do endereco e
 * a credencial da visita. Ela mostra de qual empresa o convite veio, com a marca
 * daquela empresa, porque quem recebe precisa reconhecer o remetente antes de
 * escolher uma senha.
 *
 * Quando o endereco ja tem identidade na plataforma, a tela nao pede senha nova: o
 * convite vincula a pessoa a mais uma empresa, e trocar a senha de uma identidade
 * existente transformaria qualquer convite em uma redefinicao de senha de terceiro.
 * Ela pede a senha atual da conta, que o servidor confere: o vinculo novo so vai
 * para quem e dono da conta.
 */
export function InvitationAcceptPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { status } = useAuth();

  const [nome, setNome] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [concluido, setConcluido] = useState(false);

  const consulta = useQuery({
    queryKey: ['invitation', token],
    queryFn: ({ signal }) => invitations.preview(token, signal),
    retry: false,
  });

  // Quem ja esta autenticado nao tem o que fazer aqui: o convite pertence a outra
  // identidade, e aceitar por cima de uma sessao aberta so causaria confusao.
  if (status === 'authenticated') {
    return <Navigate to="/" replace />;
  }

  const convite = consulta.data;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    void submeter();
  }

  async function submeter() {
    setErro(null);

    if (convite && !convite.known_identity) {
      if (senha !== confirmacao) {
        setErro('As duas senhas precisam ser iguais.');
        return;
      }
    }

    setEnviando(true);
    try {
      await invitations.accept(token, {
        name: nome || convite?.name || '',
        password: senha,
      });
      setConcluido(true);
    } catch (causa) {
      setErro(
        causa instanceof ApiError
          ? causa.message
          : 'Nao foi possivel concluir o convite. Tente novamente.',
      );
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas px-5 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          {/* A marca da empresa que convidou, quando ela tem uma. Sem isso o convite
              chega parecendo mensagem de remetente desconhecido. */}
          {convite?.tenant.branding.logo_symbol_url ? (
            <img
              src={convite.tenant.branding.logo_symbol_url}
              alt=""
              className="h-11 w-auto"
            />
          ) : (
            <LogoSymbol size={44} />
          )}
          {convite ? (
            <p className="text-xs text-ink-500">
              {convite.tenant.branding.display_name || convite.tenant.name}
            </p>
          ) : null}
        </div>

        {consulta.isPending ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : consulta.isError ? (
          <Alert tone="danger" title="Convite indisponivel">
            Este convite nao esta mais disponivel. Ele pode ter vencido, sido usado ou
            revogado. Peca um novo a quem administra a empresa.
          </Alert>
        ) : concluido ? (
          <div className="flex flex-col gap-4">
            <Alert tone="success" title="Acesso liberado">
              {convite?.known_identity
                ? 'Voce ja tem uma conta na plataforma e agora tambem tem acesso a esta empresa. Entre com a sua senha de sempre.'
                : 'Sua conta foi criada. Entre com o e-mail do convite e a senha que voce acabou de escolher.'}
            </Alert>
            <Button
              size="lg"
              className="w-full"
              onClick={() => void navigate('/entrar')}
            >
              Ir para o acesso
            </Button>
          </div>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-ink-900">
              {convite?.known_identity ? 'Confirmar seu acesso' : 'Criar seu acesso'}
            </h1>
            <p className="mt-1 mb-7 text-sm text-ink-500">
              Voce foi convidado para{' '}
              {convite?.tenant.branding.display_name || convite?.tenant.name}.
            </p>

            <form onSubmit={enviar} noValidate className="flex flex-col gap-4">
              {erro ? <Alert tone="danger">{erro}</Alert> : null}

              <Field
                label="E-mail"
                value={convite?.email ?? ''}
                readOnly
                disabled
                hint="O convite vale so para este endereco."
              />

              {convite?.known_identity ? (
                <>
                  <Alert tone="info" title="Voce ja tem uma conta">
                    Este e-mail ja existe na plataforma. Digite a senha da sua conta
                    para adicionar esta empresa ao seu acesso. A senha continua a mesma:
                    ninguem consegue troca-la por aqui.
                  </Alert>
                  <Field
                    label="Senha da sua conta"
                    type="password"
                    required
                    autoComplete="current-password"
                    value={senha}
                    onChange={(evento) => setSenha(evento.target.value)}
                  />
                </>
              ) : (
                <>
                  <Field
                    label="Nome completo"
                    required
                    autoComplete="name"
                    value={nome || convite?.name || ''}
                    onChange={(evento) => setNome(evento.target.value)}
                  />
                  <Field
                    label="Senha"
                    type="password"
                    required
                    autoComplete="new-password"
                    value={senha}
                    hint={POLITICA_DE_SENHA}
                    onChange={(evento) => setSenha(evento.target.value)}
                  />
                  <Field
                    label="Confirmar senha"
                    type="password"
                    required
                    autoComplete="new-password"
                    value={confirmacao}
                    onChange={(evento) => setConfirmacao(evento.target.value)}
                  />
                </>
              )}

              <Button
                type="submit"
                size="lg"
                loading={enviando}
                className="mt-2 w-full"
              >
                {convite?.known_identity ? 'Confirmar acesso' : 'Criar acesso'}
              </Button>
            </form>

            <p className="mt-6 text-center text-2xs text-ink-500">
              Sua senha e escolhida por voce. Quem administra a empresa nao a define nem
              consegue enxerga-la.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
